import React from 'react';
import styled from 'styled-components';
import {
  Breakpoints,
  BreakpointWrapper,
  CTA,
  Container,
  ContainerPadding,
  ContainerGutter,
  Divider as CoreDivider,
  Layout,
  Text,
  TokenTextAppearance,
} from '@volkswagen-onehub/components-core';
import ScrollHintArrow from '../ScrollHintArrow';

const EVENT_IMAGE =
  'https://assets.volkswagen.com/is/image/volkswagenag/imagen_evento_50fest_1920x1080px-1?Zml0PWNyb3AlMkMxJndpZD0xOTIwJmhlaT0xMDgwJmZtdD1wbmctYWxwaGEmYmZjPW9mZiY1M2Qx';

const heroLayoutAppearance = {
  [Breakpoints.default]: [{ name: 'hero', columns: 24 }],
  [Breakpoints.b1920]: [
    { name: '.', columns: 2 },
    { name: 'hero', columns: 20 },
    { name: '.', columns: 2 },
  ],
};

const detailLayoutAppearance = {
  [Breakpoints.default]: [
    { name: '.', columns: 1 },
    { name: 'divider', columns: 22 },
    { name: '.', columns: 1 },
    { name: '.', columns: 1 },
    { name: 'left', columns: 22 },
    { name: '.', columns: 1 },
    { name: '.', columns: 1 },
    { name: 'right', columns: 22 },
    { name: '.', columns: 1 },
  ],
  [Breakpoints.b960]: [
    { name: '.', columns: 2 },
    { name: 'divider', columns: 20 },
    { name: '.', columns: 2 },
    { name: '.', columns: 2 },
    { name: 'left', columns: 9 },
    { name: '.', columns: 2 },
    { name: 'right', columns: 9 },
    { name: '.', columns: 2 },
  ],
  [Breakpoints.b1920]: [
    { name: '.', columns: 4 },
    { name: 'divider', columns: 16 },
    { name: '.', columns: 4 },
    { name: '.', columns: 4 },
    { name: 'left', columns: 7 },
    { name: '.', columns: 2 },
    { name: 'right', columns: 7 },
    { name: '.', columns: 4 },
  ],
};

const RootSection = styled.section`
  position: relative;
  background: #c90010;
  color: #000000;
  overflow: hidden;
  scroll-margin-top: clamp(16px, 2.5vw, 40px);
`;

const HeroSection = styled.div`
  width: 100%;
  padding-top: var(--size-grid001);
`;

const SectionContainer = styled(Container)`
  position: relative;
  width: 100%;
`;

const ScrollHintArrowLayer = styled.div`
  position: absolute;
  inset: 0;
  z-index: 2;
  pointer-events: none;
`;

const DetailLayout = styled(Layout)`
  width: 100%;
  row-gap: 24px;
  align-items: stretch;

  @media screen and (min-width: 960px) {
    row-gap: clamp(28px, 2vw, 48px);
  }
`;

const FullWidthLayout = styled(Layout)`
  width: 100%;
  max-width: none;
`;

const HeroContent = styled.div`
  --hero-font-size: 9vw;
  --hero-line-height: 9.5vw;
  --hero-letter-spacing: -0.02em;
  width: 100%;
  display: grid;
  gap: clamp(10px, 1.8vw, 18px);

  @media screen and (min-width: 960px) {
    --hero-font-size: 7vw;
    --hero-line-height: 7.5vw;
  }
`;

const HeroLine = styled.div`
  width: 100%;
  display: grid;
  gap: clamp(12px, 2vw, 20px);
  align-items: center;
  grid-template-columns: auto minmax(0, 1fr);
`;

const HeroLineReverse = styled(HeroLine)`
  grid-template-columns: minmax(0, 1fr) auto;
`;

const VisualColumn = styled.div`
  display: grid;
  align-content: start;

  @media screen and (min-width: 960px) {
    height: 100%;
    align-content: stretch;
  }
`;

const ContentColumn = styled.div`
  display: grid;
  gap: 18px;
  align-content: start;
  min-width: 0;
  width: 100%;

  @media screen and (min-width: 960px) {
    height: 100%;
  }
`;

const Title = styled.h2`
  margin: 0;
  font-family: 'Futura Condensed Extra Bold', Futura, Arial, sans-serif;
  font-size: clamp(52px, 9vw, 120px);
  line-height: 0.88;
  text-transform: uppercase;
  color: #000000;
`;

const HeroTitle = styled(Title)`
  font-size: var(--hero-font-size);
  line-height: var(--hero-line-height);
  letter-spacing: var(--hero-letter-spacing);
  white-space: nowrap;
  font-family: Futura, Arial, sans-serif;

  @media screen and (min-width: 1600px) {
    font-family: Futura, Arial, sans-serif;
    font-style: normal;
    font-weight: 700;
  }
`;

const HeroTitleLeft = styled(HeroTitle)`
  padding-left: clamp(12px, 4vw, 24px);

  @media screen and (min-width: 960px) {
    padding-left: clamp(28px, 4vw, 96px);
  }
`;

const HeroTitleRight = styled(HeroTitle)`
  padding-right: clamp(12px, 4vw, 24px);
  text-align: right;

  @media screen and (min-width: 960px) {
    padding-right: clamp(28px, 4vw, 96px);
  }
`;

const TitleAccent = styled.span`
  color: #000000;
`;

const GtiSvgGroup = styled.span`
  display: inline-flex;
  align-items: flex-end;
  gap: 0.32em;
  height: 6.5vw;
  flex: 0 0 auto;
  margin-right: 0.14em;
  vertical-align: 0.02em;

  @media screen and (min-width: 960px) {
    height: 5.5vw;
  }
    
  svg {
    height: 100%;
    width: auto;
    display: block;
  }
`;

const Stripe = styled.div`
  position: relative;
  width: 100%;
  min-height: 28px;
  background: #c90010;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: -18px;
    right: -18px;
    background: repeating-linear-gradient(-55deg, #000000 0 5px, transparent 5px 18px);
  }
`;

const HeroStripe = styled(Stripe)`
  min-height: calc(var(--hero-line-height) * 0.7);
  height: calc(var(--hero-line-height) * 0.7);
`;

const Divider = styled(CoreDivider)`
  width: 100%;
  height: 4px;
  background-color: #000000;
`;

const DetailDivider = styled(Divider)`
  margin-bottom: clamp(8px, 1.2vw, 16px);
`;

const EventVisual = styled.img`
  width: 100%;
  height: clamp(240px, 42vw, 420px);
  object-fit: cover;
  object-position: 50% 50%;
  display: block;

  @media screen and (min-width: 960px) {
    height: 100%;
    min-height: 100%;
  }
`;

const MetaBlock = styled.div`
  display: grid;
  gap: 20px;
  min-width: 0;
`;

const DateText = styled.p`
  margin: 0;
  font-family: 'Futura Condensed Extra Bold', Futura, Arial, sans-serif;
  font-size: clamp(36px, 5.8vw, 72px);
  line-height: 0.84;
  letter-spacing: clamp(-1px, -0.14vw, -3px);
  color: #000000;
  max-width: 100%;
`;

const StyledHeadlineText = styled(Text)`
  color: #000000;
  line-height: 1.1;
  min-width: 0;
  max-width: 100%;
  overflow-wrap: anywhere;
  text-transform: uppercase;
`;

const StyledCTA = styled(CTA)`
  background: #000000 !important;
  color: #ffffff !important;
  width: 100%;
  max-width: 100%;

  &:hover,
  &:focus,
  &:focus-visible,
  &:active {
    background: #ffffff !important;
    color: #000000 !important;
  }
`;

export const ApuntateGti50 = (): React.ReactElement => {
  const sectionRef = React.useRef<HTMLElement | null>(null);
  const visualColumnRef = React.useRef<HTMLDivElement | null>(null);
  const contentColumnRef = React.useRef<HTMLDivElement | null>(null);
  const [adaptiveVisualColumnHeight, setAdaptiveVisualColumnHeight] = React.useState<number | null>(null);

  const handleClick = (): void => {
    const formSection = document.querySelector('#formulario-module');

    if (formSection instanceof HTMLElement) {
      formSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  React.useLayoutEffect(() => {
    const updateAdaptiveLayout = (): void => {
      const visualColumn = visualColumnRef.current;
      const contentColumn = contentColumnRef.current;

      if (!visualColumn || !contentColumn) {
        return;
      }

      const viewportWidth = window.innerWidth;
      const isAdaptiveLayout = viewportWidth >= 960;

      if (!isAdaptiveLayout) {
        setAdaptiveVisualColumnHeight(null);
        return;
      }

      const targetHeight = Math.ceil(contentColumn.getBoundingClientRect().height);
      setAdaptiveVisualColumnHeight(targetHeight);
    };

    updateAdaptiveLayout();

    const resizeObserver = new ResizeObserver(() => {
      updateAdaptiveLayout();
    });

    if (visualColumnRef.current) {
      resizeObserver.observe(visualColumnRef.current);
    }

    if (contentColumnRef.current) {
      resizeObserver.observe(contentColumnRef.current);
    }

    window.addEventListener('resize', updateAdaptiveLayout);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateAdaptiveLayout);
    };
  }, []);

  const visualColumnStyle =
    adaptiveVisualColumnHeight !== null
      ? ({ height: `${adaptiveVisualColumnHeight}px` } as React.CSSProperties)
      : undefined;

  return (
    <RootSection id="apuntate-gti50-module" ref={sectionRef}>
      <HeroSection>
        <FullWidthLayout
          appearance={heroLayoutAppearance}
          allowOverflowingContent
          data-testid="apuntate-gti50-hero-layout"
        >
          <HeroContent>
            <HeroLine>
              <HeroTitleLeft>Apúntate al</HeroTitleLeft>
              <HeroStripe aria-hidden />
            </HeroLine>

            <HeroLineReverse>
              <HeroStripe aria-hidden />
              <HeroTitleRight>
                <GtiSvgGroup aria-label="GTI">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 89 76" fill="none" aria-hidden>
                    <path
                      d="M73.1557 17.7244C64.4685 16.1964 55.383 15.4324 46.6073 15.4324C43.1855 15.4324 39.7784 15.5343 36.3714 15.9078C33.1118 16.2643 29.4688 16.9434 26.3862 18.4035C23.0971 19.9484 20.5308 22.3252 18.9231 26.0942C17.4335 29.6255 17.0352 33.7849 17.0352 37.5879C17.0352 41.4247 17.4482 45.5842 18.9231 49.1494C20.5013 52.9354 23.0234 55.4141 26.2977 57.0439C29.395 58.6058 33.0086 59.3189 36.3124 59.7094C39.7489 60.1168 43.1707 60.2017 46.6073 60.2017C50.6485 60.2017 54.675 60.0659 58.7015 59.7773C62.2856 59.5226 67.1823 59.0472 70.7221 58.3172C71.2973 58.1984 72.02 58.0456 72.4625 57.4683C72.905 56.8911 72.9197 56.0422 72.9492 55.3801C73.0377 52.7996 73.082 50.219 73.082 47.6215H53.6573V33.021H88.3178C88.539 41.4078 88.5833 50.8811 88.2441 59.2679C88.1261 62.239 87.9343 65.6005 86.0907 68.1301C84.2323 70.6767 81.4005 71.4407 78.8489 72.0179C68.554 74.3778 57.0644 75.1078 46.622 75.1078C41.2533 75.1078 35.6339 74.9041 30.2947 74.0722C24.9998 73.2573 19.4689 71.7633 14.6459 68.8941C3.64304 62.3578 0 50.7962 0 37.5709C0 24.2267 3.68728 12.9537 14.7344 6.43442C19.5426 3.5992 25.0588 2.05426 30.3242 1.17144C35.6487 0.288615 41.2533 0 46.6073 0C53.3476 0 60.3092 0.458389 67.0053 1.30726C73.2442 2.08821 81.0317 3.46338 87.1084 5.41578V23.989H73.1557V17.7244Z"
                      fill="black"
                    />
                  </svg>
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 84 72" fill="none" aria-hidden>
                    <path
                      d="M33.5838 14.6005V56.976H19.8376V71.2709H63.5393V56.976H49.7931V14.6005H70.5156V22.3592H83.3768V0H0V22.3592H12.8465V14.6005H33.5838Z"
                      fill="black"
                    />
                  </svg>
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 44 72" fill="none" aria-hidden>
                    <path
                      d="M13.7462 14.957H0V0H43.7017V14.957H29.9555V56.976H43.7017V71.2709H0V56.976H13.7462V14.957Z"
                      fill="black"
                    />
                  </svg>
                </GtiSvgGroup>{' '}
                <TitleAccent>50FEST</TitleAccent>
              </HeroTitleRight>
            </HeroLineReverse>
          </HeroContent>
        </FullWidthLayout>
      </HeroSection>

      <SectionContainer
        padding={{
          top: ContainerGutter.grid001,
          bottom: ContainerPadding.dynamic0250,
        }}
        wrap="always"
        stretchContent
      >
        <DetailLayout appearance={detailLayoutAppearance} data-testid="apuntate-gti50-layout">
          <DetailDivider />

          <VisualColumn ref={visualColumnRef} style={visualColumnStyle}>
            <EventVisual src={EVENT_IMAGE} alt="Golf GTI sobre pista en una imagen promocional del evento" />
          </VisualColumn>

          <ContentColumn ref={contentColumnRef}>
            <StyledHeadlineText appearance={TokenTextAppearance.headline300} bold>
              Tickets a partir de 19,76€. <br />
              Podrás sumar, a tu entrada general, una experiencia de conducción en pista o elegir el pack VIP con todo
              incluido.
            </StyledHeadlineText>
            <DetailDivider />

            <MetaBlock>
              <DateText>27.06.2026</DateText>
              <StyledHeadlineText appearance={TokenTextAppearance.headline300} bold>
                CIRCUITO DE BARCELONA/ CATALUNYA
              </StyledHeadlineText>
            </MetaBlock>

            <StyledCTA
              tag="button"
              emphasis="primary"
              type="button"
              aria-label="Apúntate al GTI Day 2026"
              onClick={handleClick}
            >
              Apúntate
            </StyledCTA>
          </ContentColumn>
        </DetailLayout>

        <ScrollHintArrowLayer>
          <ScrollHintArrow trigger={sectionRef} targetSelector="#gallery-gti50-module" color="#ffffff" />
        </ScrollHintArrowLayer>
      </SectionContainer>
    </RootSection>
  );
};
